import {
  Box,
  Card,
  IconButton,
  Popover,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { KeyedCaseValueCalc, formatRelativeTime, isNotUndefined } from "Backend";
import { displayCalcValue } from "components/FormField/displayCellValue";
import MetricsToolbar, { FullCalcCard, MetricProps } from "components/MetricsToolbar";
import {
  CalcCaseProperty,
  CaseDisplayGroup,
  CaseDisplayGroupItem,
  CaseDisplayGroupType,
  CustomCaseProperty,
  KeyValue,
} from "generated/graphql";
import { useCallback, useState } from "react";
import { Fragment } from "react/jsx-runtime";
import { useAppSelector } from "redux/hooks";
import { selectCase, selectCaseCalcs, selectCaseProperties } from "redux/slices/case";
import { selectCalcProperties, selectCasePropertySchemas, selectClientLensAssignees } from "redux/slices/clientLens";
import theme from "theme";
import { P } from "theme/typography";
import EditCaseInfoDialog from "./EditCaseInfoDialog";
import { Edit } from "components/Icons";
import DescriptionTooltip from "components/FormField/DescriptionTooltip";
import { stripNullEmpty, stripNull } from "helpers/coll";
import { usePopover } from "helpers/hooks/usePopover";
import { OpenInNew } from "@mui/icons-material";

interface RowWithMetrics {
  row: string;
  metrics: (MetricProps | undefined)[];
  validMetricsCount: number;
  description?: string;
}

function MetricValueCell({ metric }: { metric: MetricProps }) {
  const popover = usePopover();
  const canShowFullCalcCard = metric.explanation !== undefined || metric.linkedValueGroups !== undefined;

  return (
    <>
      <TableCell>
        <div className="flex gap-2 items-center">
          <Tooltip title={`Updated ${metric.updated}`}>
            <span>{metric.value}</span>
          </Tooltip>
          {canShowFullCalcCard && (
            <Tooltip title="Details">
              <IconButton
                size="small"
                className="text-gray-200 invisible group-hover:visible"
                onClick={(evt) => popover.onPopoverOpen(evt.currentTarget)}>
                <OpenInNew />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </TableCell>
      <Popover
        open={popover.isOpen}
        anchorEl={popover.anchorEl}
        onClose={popover.onPopoverClose}
        sx={{ maxWidth: 1200 }}>
        <FullCalcCard
          title={metric.title}
          explanation={metric.explanation}
          linkedValueGroups={metric.linkedValueGroups}
          description={metric.description}
          value={metric.value}
        />
      </Popover>
    </>
  );
}

export function GroupTable({
  columns,
  rows,
  columnDescriptions,
  rowDescriptions,
  metrics,
}: {
  columns: string[];
  rows: string[];
  columnDescriptions: string[];
  rowDescriptions: string[];
  metrics: MetricProps[];
}) {
  // const showableRows = rows.filter((row) => !row.isEmpty);
  const findMetric = useCallback(
    (colIndex: number, rowIndex: number) => {
      return metrics.find((m) => m.colIndex === colIndex && m.rowIndex === rowIndex);
    },
    [columns, rows, metrics]
  );

  const rowsWithMetrics: RowWithMetrics[] = rows.map((row, rowIndex) => {
    const rowMetrics = columns.map((_, colIndex) => findMetric(colIndex, rowIndex));
    const validMetrics = rowMetrics.filter(isNotUndefined);
    return {
      row,
      metrics: rowMetrics,
      validMetricsCount: validMetrics.length,
      description: rowDescriptions[rowIndex],
    };
  });

  return rows.length === 0 ? (
    <></>
  ) : (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell />
          {columns.map((c, cIndex) => (
            <TableCell key={c}>
              <div className="flex items-center gap-2">
                <p className="text-xs">{c}</p>
                {columnDescriptions[cIndex] !== undefined && (
                  <DescriptionTooltip description={columnDescriptions[cIndex]} />
                )}
              </div>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rowsWithMetrics.map((rowWithMetrics) => {
          return rowWithMetrics.validMetricsCount > 0 ? (
            <TableRow key={rowWithMetrics.row} className="group">
              <TableCell>
                <div className="flex items-center gap-2">
                  <p className="text-xs">{rowWithMetrics.row}</p>
                  {rowWithMetrics.description !== undefined && (
                    <DescriptionTooltip description={rowWithMetrics.description} />
                  )}
                </div>
              </TableCell>
              {rowWithMetrics.metrics.map((metric, metricIndex) => {
                return metric !== undefined ? (
                  <MetricValueCell key={`${rowWithMetrics.row}-${metricIndex}`} metric={metric} />
                ) : (
                  <TableCell key={`${rowWithMetrics.row}-${metricIndex}`} />
                );
              })}
            </TableRow>
          ) : (
            <TableRow key={rowWithMetrics.row} />
          );
        })}
      </TableBody>
    </Table>
  );
}

function metricFromGroupItem({
  item,
  calcProperties,
  caseProperties,
  calcValues,
  propertyValues,
}: {
  item: CaseDisplayGroupItem;
  calcProperties: CalcCaseProperty[];
  caseProperties: CustomCaseProperty[];
  calcValues: KeyedCaseValueCalc[];
  propertyValues: KeyValue[];
}): MetricProps | undefined {
  const foundCaseProperty = caseProperties.find((cp) => cp.key === item.casePropertyKey);
  const foundCalcProperty = calcProperties.find((cp) => cp.key === item.caseCalcKey);
  const calc = calcValues.find((cv) => cv.key === item.caseCalcKey);
  const property = propertyValues.find((pv) => pv.key === item.casePropertyKey);

  if (foundCaseProperty !== undefined && property !== undefined) {
    return {
      title: foundCaseProperty.displayName,
      value: property.value,
      description: foundCaseProperty.description || undefined,
      rowIndex: item.rowIndex,
      colIndex: item.colIndex,
      caseProperty: foundCaseProperty,
    };
  } else if (foundCalcProperty !== undefined && calc !== undefined) {
    return {
      title: foundCalcProperty.displayName,
      value: displayCalcValue(calc),
      description: foundCalcProperty.description || undefined,
      updated: formatRelativeTime(calc.timestamp),
      rowIndex: item.rowIndex,
      colIndex: item.colIndex,
      explanation: stripNullEmpty(calc?.explanation),
      linkedValueGroups: stripNull(calc?.linkedValueGroups),
    };
  } else {
    return undefined;
  }
}

interface GroupWithMetrics {
  group: CaseDisplayGroup;
  metrics: MetricProps[];
  caseProperties: CustomCaseProperty[];
}

function GroupTitle({ group }: { group: CaseDisplayGroup }) {
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <P variant="smSemiBold">{group.name}</P>
      <DescriptionTooltip description={group.description} />
    </Box>
  );
}

function findGroupCaseProperties(
  group: CaseDisplayGroup,
  casePropertySchemas: CustomCaseProperty[]
): CustomCaseProperty[] {
  return group.items.map((i) => casePropertySchemas.find((cp) => cp.key === i.casePropertyKey)).filter(isNotUndefined);
}

export function CaseDisplayGroups({ groups, sx }: { groups: CaseDisplayGroup[]; sx?: SxProps }) {
  const myCase = useAppSelector(selectCase);
  const assignees = useAppSelector(selectClientLensAssignees);
  const calcProperties = useAppSelector(selectCalcProperties);
  const caseProperties = useAppSelector(selectCasePropertySchemas);
  const calcValues = useAppSelector(selectCaseCalcs);
  const propertyValues = useAppSelector(selectCaseProperties);
  const groupsWithMetrics: GroupWithMetrics[] = groups.map((group) => {
    const metrics = group.items
      .map((item) =>
        metricFromGroupItem({
          item,
          calcProperties,
          caseProperties,
          calcValues,
          propertyValues,
        })
      )
      .filter(isNotUndefined);
    const groupCaseProperties = findGroupCaseProperties(group, caseProperties);
    return {
      group,
      metrics,
      caseProperties: groupCaseProperties,
    };
  });
  const atLeastOneGroupHasData = groupsWithMetrics.reduce((count, g) => count + g.metrics.length, 0) > 0;
  const atLeastOneGroupHasCaseProperties = !!groupsWithMetrics.find((gwm) => gwm.caseProperties.length > 0);
  const [groupToEdit, setGroupToEdit] = useState<GroupWithMetrics | undefined>(undefined);

  return atLeastOneGroupHasData || atLeastOneGroupHasCaseProperties ? (
    <Card variant="outlined" sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2, ...sx }}>
      {groupToEdit !== undefined && groupToEdit.caseProperties.length > 0 && (
        <EditCaseInfoDialog
          open={groupToEdit !== undefined}
          close={() => setGroupToEdit(undefined)}
          caseProperties={groupToEdit.caseProperties}
          mycase={myCase}
          assignees={assignees}
          canEditCaseInfo={false}
          group={groupToEdit.group}
        />
      )}

      {groupsWithMetrics.map((groupWithMetrics) => {
        return groupWithMetrics.group.type === CaseDisplayGroupType.Card &&
          (groupWithMetrics.caseProperties.length > 0 || groupWithMetrics.metrics.length > 0) ? (
          <div className="w-full grow" key={groupWithMetrics.group.id}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: `1px solid ${theme.palette.grey[50]}`,
              }}>
              <GroupTitle group={groupWithMetrics.group} />
              {groupWithMetrics.caseProperties.length > 0 && (
                <IconButton
                  data-pendo="edit-case-display-group"
                  onClick={() => {
                    setGroupToEdit(groupWithMetrics);
                  }}
                  size="small">
                  <Edit />
                </IconButton>
              )}
            </Box>
            {groupWithMetrics.metrics.length > 0 && <MetricsToolbar metrics={groupWithMetrics.metrics} />}
          </div>
        ) : groupWithMetrics.group.type === CaseDisplayGroupType.Table &&
          Array.isArray(groupWithMetrics.group.rows) &&
          Array.isArray(groupWithMetrics.group.columns) &&
          (groupWithMetrics.metrics.length > 0 || groupWithMetrics.caseProperties.length > 0) ? (
          <Fragment key={groupWithMetrics.group.id}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: `1px solid ${theme.palette.grey[50]}`,
              }}>
              <GroupTitle group={groupWithMetrics.group} />
              {groupWithMetrics.caseProperties.length > 0 && (
                <IconButton
                  data-pendo="edit-case-display-group"
                  onClick={() => {
                    setGroupToEdit(groupWithMetrics);
                  }}
                  size="small">
                  <Edit />
                </IconButton>
              )}
            </Box>
            {groupWithMetrics.metrics.length > 0 && (
              <GroupTable
                rows={groupWithMetrics.group.rows}
                columns={groupWithMetrics.group.columns}
                metrics={groupWithMetrics.metrics}
                rowDescriptions={groupWithMetrics.group.rowDescriptions ?? []}
                columnDescriptions={groupWithMetrics.group.columnDescriptions ?? []}
              />
            )}
          </Fragment>
        ) : (
          <Fragment key={groupWithMetrics.group.id} />
        );
      })}
    </Card>
  ) : (
    <></>
  );
}
